import type { AnalyticsEventProvider, AnalyticsPageviewProvider, EventProviderToken, PageviewProviderToken } from './providers'
import { EVENT_GROUPS, EVENT_PROVIDERS, PAGEVIEW_GROUPS, PAGEVIEW_PROVIDERS, resolveProviders } from './providers'

/**
 * A declaration *describes* an event, it never sends one. Only the router turns it into an event,
 * so a call site cannot assemble its own payload and skip the context the router adds.
 *
 * The whole spec sits behind one thunk: declaring happens constantly — a list renders fifty buttons
 * — and sending is rare, so nothing is computed until something is actually sent.
 */

export type AnalyticsValue = string | number | boolean | null | undefined

/**
 * Arrays and nested objects are allowed because events carry them — an announcement filter goes out
 * whole. Flattening is a sink's job: `serializeEventData` has always unfolded both for trackings.
 */
export type AnalyticsData = { [key: string]: AnalyticsValue | readonly AnalyticsValue[] | AnalyticsData | readonly AnalyticsData[] }

export type AnalyticsSpec = { type: string; data?: AnalyticsData }
export type AnalyticsPageviewSpec = AnalyticsSpec & { url: string; path: string; title: string }

/** Tagged rather than duck-typed, so a bare object cannot be passed where a declaration belongs. */
const DECLARATION = Symbol('analytics.declaration')

export abstract class AnalyticsDeclaration<S extends AnalyticsSpec = AnalyticsSpec, P extends string = string> {
  readonly [DECLARATION] = true

  constructor(
    readonly spec: () => S,
    readonly providers: readonly P[]
  ) {}

  abstract readonly kind: AnalyticsKind
}

export type AnalyticsKind = 'custom' | 'pageview'

export class AnalyticsEventDeclaration extends AnalyticsDeclaration<AnalyticsSpec, AnalyticsEventProvider> {
  readonly kind = 'custom'

  /** Replaces the default set outright — see `resolveProviders` for how the tokens fold. */
  set(...tokens: [EventProviderToken, ...EventProviderToken[]]) {
    return new AnalyticsEventDeclaration(this.spec, nonEmpty(resolveProviders(EVENT_PROVIDERS, EVENT_GROUPS, tokens), tokens))
  }

  /**
   * Dimensions the *control* owns, not the declarer — where a link actually went. They land under
   * the declared data, so a declared field still wins, and this is not a way to assemble a payload:
   * it takes a declaration it did not write and can only add to it.
   */
  withData(extra: AnalyticsData) {
    return new AnalyticsEventDeclaration(() => {
      const resolved = this.spec()
      return { ...resolved, data: { ...extra, ...resolved.data } }
    }, this.providers)
  }
}

export class AnalyticsPageviewDeclaration extends AnalyticsDeclaration<AnalyticsPageviewSpec, AnalyticsPageviewProvider> {
  readonly kind = 'pageview'

  set(...tokens: [PageviewProviderToken, ...PageviewProviderToken[]]) {
    return new AnalyticsPageviewDeclaration(this.spec, nonEmpty(resolveProviders(PAGEVIEW_PROVIDERS, PAGEVIEW_GROUPS, tokens), tokens))
  }
}

/**
 * An event paired with the handler it belongs to. A component takes one of these instead of an
 * event prop beside a handler prop, so a control can only report something it can also do, however
 * far the pair was forwarded.
 *
 * `Args` is what the control hands its handler — a DOM event, usually. A handler that ignores it
 * still fits, so the common case stays `() => void`.
 */
export type AnalyticsAction<Args extends readonly unknown[] = []> = {
  event: AnalyticsEventDeclaration
  handler: (...args: Args) => void
}

export const analyticsEvent = (spec: () => AnalyticsSpec) => new AnalyticsEventDeclaration(spec, EVENT_GROUPS.default)

export const analyticsPageview = (spec: () => AnalyticsPageviewSpec) => new AnalyticsPageviewDeclaration(spec, PAGEVIEW_GROUPS.default)

/**
 * Events a control always sends one of, declared together. A confirmation sends accept or decline
 * and nothing else, so the pair belongs in one place: one call site to read, one set of providers,
 * and no way for half of it to be renamed without the other.
 *
 * It hands back real declarations, so a caller destructures the one it is about to send and
 * everything downstream — `.set(...)`, `AnalyticsAction`, the router — is unchanged.
 */
export const analyticsEventGroup = <K extends string>(
  specs: { [Key in K]: () => AnalyticsSpec },
  providers?: [EventProviderToken, ...EventProviderToken[]]
): { [Key in K]: AnalyticsEventDeclaration } =>
  Object.fromEntries(
    Object.entries<() => AnalyticsSpec>(specs).map(([key, spec]) => {
      const declaration = analyticsEvent(spec)
      return [key, providers ? declaration.set(...providers) : declaration]
    })
  ) as { [Key in K]: AnalyticsEventDeclaration }

/**
 * A control that deliberately reports nothing. Every interaction prop demands a declaration, and
 * without a way to say "none" that requirement invents names. Explicit on purpose: a *forgotten*
 * declaration stays an error, and only a deliberate absence compiles.
 */
export class NoAnalyticsDeclaration extends AnalyticsEventDeclaration {
  constructor() {
    super(() => ({ type: '' }), [])
  }
}

export const noEvent = new NoAnalyticsDeclaration()

export const isAnalyticsDeclaration = (value: unknown): value is AnalyticsDeclaration => typeof value === 'object' && value !== null && DECLARATION in value

/** An event routed nowhere is dead code, and almost always a `.set('-x')` written without a base. */
const nonEmpty = <P extends string>(providers: readonly P[], tokens: readonly string[]): readonly P[] => {
  if (!providers.length) throw new Error(`AnalyticsDeclaration.set: (${tokens.join(', ')}) resolves to no providers — use noEvent to report nothing`)
  return providers
}
